import React, { useEffect, useState } from 'react';
import { Store } from '../../interfaces/store.interface';
import {
    createStore,
    updateStore,
    deleteStore,
    getAllInstitutionStores
} from '../../services/store.service';
import { Modal, Button, Box, Tooltip, IconButton, TextField } from '@mui/material';
import { Delete, Add, Edit } from '@mui/icons-material';
import { IInstitution, IInstitutionResponse } from '../../interfaces/institution.interface';
import { InstitutionService } from '../../services/institution.service';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import ScrollableTable from '../common/ScrollableTable';
import SearchableSelect from '../common/SearchableSelect';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

const StoreList: React.FC = () => {
    const toast = useToast();
    const isSystemAdmin = AuthService.getCurrentUser()?.roles?.includes('SystemAdmin');
    const [stores, setStores] = useState<Store[]>([]);
    const [institutions, setInstitutions] = useState<IInstitution[]>([]);
    const [loading, setLoading] = useState(true);
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedStore, setSelectedStore] = useState<Store | null>(null);
    const [formData, setFormData] = useState({
        storeName: '',
        address: '',
        description: '',
        institutionId: ''
    });
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        totalPages: 0,
        totalElements: 0
    });

    const fetchStores = async (page: number) => {
        try {
            setLoading(true);
            const response = await getAllInstitutionStores(page, 10);
            setStores(response.content);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements
            });
        } catch (err) {
            toast.showApiError(err, 'Failed to load stores');
            console.error('Error fetching stores:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchInstitutions = async () => {
        try {
            const response: IInstitutionResponse = await InstitutionService.getInstitutions(0, 100);
            setInstitutions(response.content);
        } catch (err) {
            console.error('Error fetching institutions:', err);
        }
    };

    useEffect(() => {
        fetchStores(0);
        fetchInstitutions();
    }, []);

    const handleOpenFormModal = (store?: Store) => {
        if (store) {
            setSelectedStore(store);
            setFormData({
                storeName: store.storeName,
                address: store.address,
                description: store.description,
                institutionId: store.institutionId || ''
            });
        } else {
            setSelectedStore(null);
            setFormData({
                storeName: '',
                address: '',
                description: '',
                institutionId: ''
            });
        }
        setIsFormModalOpen(true);
    };

    const handleCloseFormModal = () => {
        setIsFormModalOpen(false);
        setSelectedStore(null);
        setFormData({
            storeName: '',
            address: '',
            description: '',
            institutionId: ''
        });
    };

    const handleOpenDeleteModal = (store: Store) => {
        setSelectedStore(store);
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setSelectedStore(null);
    };

    const handleDelete = async () => {
        if (!selectedStore) return;

        try {
            await deleteStore(selectedStore.id!);
            await fetchStores(pageInfo.currentPage);
            handleCloseDeleteModal();
        } catch (error) {
            console.error('Error deleting store:', error);
            toast.showApiError(error, 'Failed to delete store');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (selectedStore) {
                await updateStore(selectedStore.id!, formData);
            } else {
                await createStore(formData);
            }
            await fetchStores(pageInfo.currentPage);
            handleCloseFormModal();
        } catch (error) {
            console.error('Error saving store:', error);
            toast.showApiError(error, 'Failed to save store');
        }
    };

    if (loading) {
        return <div data-testid="loading-state">Loading...</div>;
    }

    return (
        <div style={{ padding: '20px' }} data-help-page="store.allStores.page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' , flexWrap: 'wrap', gap: '8px' }}>
                <h2>Store List</h2>
                <Button
                    data-help="store.allStores.create"
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => handleOpenFormModal()}
                >
                    Create Store
                </Button>
            </div>

            <div style={{ marginBottom: '20px' }}>
                Total Stores: {pageInfo.totalElements}
            </div>

            <div data-help-section="store.allStores.table">
            <ScrollableTable>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }} data-testid="stores-table">
                <thead>
                    <tr>
                        <th data-help="store.allStores.col.name" style={tableHeaderStyle}>Store Name</th>
                        <th data-help="store.allStores.col.address" style={tableHeaderStyle}>Address</th>
                        <th data-help="store.allStores.col.description" style={tableHeaderStyle}>Description</th>
                        <th data-help="store.allStores.col.actions" style={tableHeaderStyle}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {stores.map(store => (
                        <tr key={store.id} data-testid={`store-row-${store.id}`}>
                            <td style={tableCellStyle}>{store.storeName}</td>
                            <td style={tableCellStyle}>{store.address}</td>
                            <td style={tableCellStyle}>{store.description}</td>
                            <td style={tableCellStyle}>
                                <Tooltip title="Edit Store" arrow>
                                    <IconButton
                                        data-help="store.allStores.edit"
                                        size="small"
                                        onClick={() => handleOpenFormModal(store)}
                                        color="primary"
                                        aria-label="Edit Store"
                                    >
                                        <Edit />
                                    </IconButton>
                                </Tooltip>
                                {isSystemAdmin && (
                                    <Tooltip title="Delete Store" arrow>
                                        <IconButton
                                            data-help="store.allStores.delete"
                                            size="small"
                                            onClick={() => handleOpenDeleteModal(store)}
                                            color="error"
                                            aria-label="Delete Store"
                                        >
                                            <Delete />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </ScrollableTable>
            </div>

            {/* Form Modal */}
            <Modal
                data-help-page="store.storeForm.page" open={isFormModalOpen}
                onClose={handleCloseFormModal}
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 1
                }}>
                    <h2>{selectedStore ? 'Edit Store' : 'Create Store'}</h2>
                    <form onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            data-help="store.storeForm.field.name" label="Store Name"
                            value={formData.storeName}
                            onChange={(e) => setFormData({ ...formData, storeName: e.target.value })}
                            required
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            data-help="store.storeForm.field.address" label="Address"
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            required
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            data-help="store.storeForm.field.description" label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            required
                            margin="normal"
                            multiline
                            rows={3}
                        />
                        <SearchableSelect<IInstitution> data-help="store.storeForm.field.institution"
                            options={institutions}
                            value={formData.institutionId}
                            onChange={id => setFormData({ ...formData, institutionId: id })}
                            getOptionLabel={institution => institution.name}
                            label="Institution"
                            required
                            sx={{ mt: 2, mb: 1 }}
                        />
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button data-help="store.storeForm.cancel" onClick={handleCloseFormModal}>
                                Cancel
                            </Button>
                            <Button data-help="store.storeForm.save" type="submit" variant="contained" color="primary">
                                {selectedStore ? 'Update' : 'Create'}
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Modal>

            {/* Delete Confirmation Modal */}
            {isSystemAdmin && (
                <Modal
                    data-help-page="store.allStores.deleteModal" open={isDeleteModalOpen}
                    onClose={handleCloseDeleteModal}
                >
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 1
                    }}>
                        <h2>Delete Store</h2>
                        <p>Are you sure you want to delete this store?</p>
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button data-help="store.allStores.deleteCancel" onClick={handleCloseDeleteModal}>
                                Cancel
                            </Button>
                            <Button
                                data-help="store.allStores.deleteConfirm"
                                onClick={handleDelete}
                                variant="contained"
                                color="error"
                            >
                                Delete
                            </Button>
                        </Box>
                    </Box>
                </Modal>
            )}
        </div>
    );
};

export default StoreList;
